import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../api/client";
import AdminDeployPanel from "../components/admin/AdminDeployPanel";

type ServiceStatus = {
  name: string;
  active: string;
  enabled: string;
};

type SystemStatus = {
  timestamp: string;
  hostname: string;
  user: string;
  loadAverage: string;
  memory?: {
    totalMb: number;
    usedMb: number;
    freeMb: number;
  };
  disk?: {
    usedPercent: number;
    usedHuman: string;
    availableHuman: string;
  };
  services?: ServiceStatus[];
  processes?: {
    runAllCrawlers: boolean;
    testKorStock: boolean;
    test3: boolean;
    runBackfill: boolean;
    dartDescription: boolean;
    korStockDescription: boolean;
    chromeResidual: boolean;
  };
  files?: {
    envExists: boolean;
    venvPythonExists: boolean;
    sectorKeywordsExists: boolean;
  };
};

type TrafficStatus = {
  timestamp: string;
  windowMinutes: number;
  summary?: {
    totalRequests: number;
    uniqueIps: number;
    sessionCount: number;
    avgSessionSeconds: number;
    singlePageSessions: number;
  };
  statusCounts?: Record<string, number>;
  topUrls?: { url: string; count: number }[];
  topLandingPages?: { url: string; count: number }[];
  topExitPages?: { url: string; count: number }[];
  error?: string;
};

type DailyIpVisitItem = {
  ip: string;
  requestCount: number;
  sessionCount: number;
  pageViewCount: number;
  firstSeen: string;
  lastSeen: string;
  avgSessionSeconds: number;
  maxSessionSeconds: number;
  landingPage?: string;
  exitPage?: string;
  referer?: string;
  userAgent?: string;
  country?: string;
  botSuspected?: boolean;
};

type DailyTrafficStatus = {
  date: string;
  error?: string;
  summary?: {
    totalRequests: number;
    uniqueIps: number;
    uniqueVisitors: number;
    sessionCount: number;
    avgSessionSeconds: number;
    medianSessionSeconds: number;
    bounceSessions: number;
    bounceRate: number;
    avgPagesPerSession: number;
    maxOnlineMinute?: string;
    maxOnlineCount?: number;
  };
  topIps?: DailyIpVisitItem[];
  topStayIps?: DailyIpVisitItem[];
  topEntryPages?: { url: string; count: number }[];
  topExitPages?: { url: string; count: number }[];
  referers?: { source: string; count: number }[];
  hourlyStats?: {
    hour: string;
    requests: number;
    uniqueIps: number;
    sessions: number;
  }[];
};

type SessionListResponse = {
  timestamp: string;
  windowMinutes: number;
  error?: string;
  summary?: {
    totalSessions: number;
    activeSessions: number;
    botSessions: number;
    errorSessions: number;
    bounceSessions: number;
  };
  sessions?: SessionListItem[];
};

type SessionListItem = {
  sessionId: string;
  ip: string;
  userAgent?: string;
  country?: string;
  city?: string;
  memberId?: string | null;
  firstSeen: string;
  lastSeen: string;
  durationSeconds: number;
  requestCount: number;
  pageViewCount: number;
  landingPage?: string;
  exitPage?: string;
  referer?: string;
  botSuspected?: boolean;
  active?: boolean;
  hadError?: boolean;
  hadLogin?: boolean;
  hadPayment?: boolean;
};

type SessionDetailResponse = {
  sessionId: string;
  ip: string;
  userAgent?: string;
  country?: string;
  city?: string;
  memberId?: string | null;
  firstSeen?: string;
  lastSeen?: string;
  durationSeconds?: number;
  error?: string;
  events?: SessionEvent[];
};

type SessionEvent = {
  time: string;
  method: string;
  url: string;
  status: number;
  referer?: string;
  staySeconds?: number;
  isPageView?: boolean;
  isApi?: boolean;
};

type FlowResponse = {
  timestamp: string;
  windowMinutes: number;
  error?: string;
  topFlows?: FlowItem[];
};

type FlowItem = {
  path: string[];
  count: number;
  avgDurationSeconds?: number;
  bounceRate?: number;
};

type SessionFilterType =
  | "all"
  | "active"
  | "error"
  | "bounce"
  | "adminExcluded"
  | "botOnly";

type SessionSortType =
  | "latest"
  | "duration"
  | "requests"
  | "problemFirst";

const COLORS = {
  pageBg: "#f4f7fb",
  panelBg: "#ffffff",
  panelBgSoft: "#f8fafc",
  panelBorder: "#dbe4ee",
  title: "#172033",
  text: "#344054",
  muted: "#667085",
  strong: "#101828",
  accent: "#155eef",
  accentSoft: "#eaf2ff",
  tableHeader: "#f1f5f9",
  selectedRow: "#eef4ff",
  divider: "rgba(16,24,40,0.08)",

  greenBg: "#ecfdf3",
  greenText: "#027a48",
  redBg: "#fef3f2",
  redText: "#b42318",
  yellowBg: "#fffaeb",
  yellowText: "#b54708",
  blueBg: "#eff8ff",
  blueText: "#175cd3",
};

const POLL_MS = 30000;
const DEFAULT_WINDOW_MINUTES = 10;

const getCardStyle = (isMobile: boolean): React.CSSProperties => ({
  background: COLORS.panelBg,
  borderRadius: isMobile ? 14 : 16,
  padding: isMobile ? 14 : 18,
  boxShadow: "0 1px 3px rgba(16,24,40,0.08), 0 1px 2px rgba(16,24,40,0.04)",
  border: `1px solid ${COLORS.panelBorder}`,
  minWidth: 0,
});

const getSectionTitleStyle = (isMobile: boolean): React.CSSProperties => ({
  fontSize: isMobile ? 16 : 18,
  fontWeight: 800,
  marginBottom: 12,
  color: COLORS.title,
  lineHeight: 1.35,
});

function StatusPill({
  ok,
  text,
  warn = false,
}: {
  ok: boolean;
  text: string;
  warn?: boolean;
}) {
  const style: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "5px 10px",
    borderRadius: 999,
    fontSize: 12,
    fontWeight: 800,
    whiteSpace: "nowrap",
    flexShrink: 0,
    color: warn
      ? COLORS.yellowText
      : ok
      ? COLORS.greenText
      : COLORS.redText,
    background: warn
      ? COLORS.yellowBg
      : ok
      ? COLORS.greenBg
      : COLORS.redBg,
  };
  return <span style={style}>{text}</span>;
}

function formatSeconds(sec?: number) {
  if (!sec || sec <= 0) return "0초";
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = Math.floor(sec % 60);
  if (h > 0) return `${h}시간 ${m}분 ${s}초`;
  if (m > 0) return `${m}분 ${s}초`;
  return `${s}초`;
}

function formatDateTime(value?: string) {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return value;
  return d.toLocaleString("ko-KR", { hour12: false });
}

function formatPercent(value?: number) {
  const safe = Number.isFinite(Number(value)) ? Number(value) : 0;
  return `${safe.toFixed(1)}%`;
}

function truncate(text?: string, max = 70) {
  if (!text) return "-";
  return text.length > max ? `${text.slice(0, max)}...` : text;
}

function compactUserAgent(ua?: string) {
  if (!ua) return "-";
  const lower = ua.toLowerCase();

  const browser = lower.includes("edg/")
    ? "Edge"
    : lower.includes("chrome/")
    ? "Chrome"
    : lower.includes("safari/") && !lower.includes("chrome/")
    ? "Safari"
    : lower.includes("firefox/")
    ? "Firefox"
    : lower.includes("postman")
    ? "Postman"
    : "Other";

  const os = lower.includes("windows")
    ? "Windows"
    : lower.includes("mac os") || lower.includes("macintosh")
    ? "Mac"
    : lower.includes("android")
    ? "Android"
    : lower.includes("iphone") || lower.includes("ipad") || lower.includes("ios")
    ? "iOS"
    : lower.includes("linux")
    ? "Linux"
    : "OS?";

  return `${browser} / ${os}`;
}

function formatVisitorId(value?: string): string {
  const raw = String(value ?? "").trim();
  if (!raw) return "-";

  if (/^\d{1,3}(?:\.\d{1,3}){3}$/.test(raw)) {
    const parts = raw.split(".");
    return `${parts[0]}.${parts[1]}.***.***`;
  }

  if (raw.includes(":")) {
    const parts = raw.split(":").filter(Boolean);
    return `${parts.slice(0, 2).join(":")}:****`;
  }

  return `VIS-${raw.slice(0, 6).toUpperCase()}`;
}

function safeArray<T>(value?: T[]) {
  return Array.isArray(value) ? value : [];
}

function useViewport() {
  const getWidth = () =>
    typeof window === "undefined" ? 1280 : window.innerWidth;

  const [width, setWidth] = useState<number>(getWidth());

  useEffect(() => {
    const onResize = () => setWidth(getWidth());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return {
    width,
    isMobile: width < 768,
    isTablet: width >= 768 && width < 1100,
  };
}

function getGridColumns(
  isMobile: boolean,
  isTablet: boolean,
  desktop: string,
  tablet: string,
  mobile: string
) {
  if (isMobile) return mobile;
  if (isTablet) return tablet;
  return desktop;
}

function TableCell({
  children,
  minWidth,
  align = "left",
  emphasize = false,
}: {
  children: React.ReactNode;
  minWidth?: number;
  align?: "left" | "center" | "right";
  emphasize?: boolean;
}) {
  return (
    <td
      style={{
        padding: "10px 12px",
        borderBottom: `1px solid ${COLORS.divider}`,
        color: emphasize ? COLORS.strong : COLORS.text,
        fontSize: 13,
        verticalAlign: "top",
        minWidth,
        textAlign: align,
        fontWeight: emphasize ? 700 : 500,
      }}
    >
      {children}
    </td>
  );
}

function TableHeaderCell({
  children,
  align = "left",
  minWidth,
}: {
  children: React.ReactNode;
  align?: "left" | "center" | "right";
  minWidth?: number;
}) {
  return (
    <th
      style={{
        padding: "10px 12px",
        textAlign: align,
        color: COLORS.title,
        background: COLORS.tableHeader,
        borderBottom: `1px solid ${COLORS.panelBorder}`,
        fontSize: 12,
        fontWeight: 800,
        whiteSpace: "nowrap",
        minWidth,
        position: "sticky",
        top: 0,
        zIndex: 1,
      }}
    >
      {children}
    </th>
  );
}

function TablePanel({
  title,
  subtitle,
  headers,
  children,
  minWidth = 720,
  actions,
}: {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  headers: Array<{
    label: React.ReactNode;
    align?: "left" | "center" | "right";
    minWidth?: number;
  }>;
  children: React.ReactNode;
  minWidth?: number;
  actions?: React.ReactNode;
}) {
  return (
    <div
      style={{
        background: COLORS.panelBg,
        borderRadius: 14,
        border: `1px solid ${COLORS.panelBorder}`,
        boxShadow: "0 1px 2px rgba(16,24,40,0.04)",
        overflow: "hidden",
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: "14px 16px 12px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: 12,
          borderBottom: `1px solid ${COLORS.panelBorder}`,
          background: COLORS.panelBg,
          flexWrap: "wrap",
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ color: COLORS.title, fontSize: 16, fontWeight: 800 }}>
            {title}
          </div>
          {subtitle ? (
            <div
              style={{
                color: COLORS.muted,
                fontSize: 12,
                marginTop: 4,
                lineHeight: 1.45,
              }}
            >
              {subtitle}
            </div>
          ) : null}
        </div>
        {actions}
      </div>

      <div
        style={{
          width: "100%",
          overflowX: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            minWidth,
            tableLayout: "auto",
          }}
        >
          <thead>
            <tr>
              {headers.map((header, index) => (
                <TableHeaderCell
                  key={index}
                  align={header.align}
                  minWidth={header.minWidth}
                >
                  {header.label}
                </TableHeaderCell>
              ))}
            </tr>
          </thead>
          <tbody>{children}</tbody>
        </table>
      </div>
    </div>
  );
}


function SectionEmpty({ text }: { text: string }) {
  return (
    <div
      style={{
        color: COLORS.muted,
        padding: "14px 2px",
        lineHeight: 1.6,
        fontSize: 14,
      }}
    >
      {text}
    </div>
  );
}

function ErrorBox({
  title,
  message,
  hint,
}: {
  title: string;
  message: string;
  hint?: string;
}) {
  return (
    <div
      style={{
        border: "1px solid rgba(255,255,255,0.12)",
        background: "#fff7ed",
        color: "#9a3412",
        borderRadius: 12,
        padding: 14,
        lineHeight: 1.6,
      }}
    >
      <div style={{ fontWeight: 800, marginBottom: 6 }}>{title}</div>
      <div>{message}</div>
      {hint ? (
        <div
          style={{
            marginTop: 8,
            fontSize: 13,
            color: "#c2410c",
          }}
        >
          {hint}
        </div>
      ) : null}
    </div>
  );
}


function ToggleButton({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      onClick={onClick}
      style={{
        border: `1px solid ${active ? COLORS.strong : COLORS.panelBorder}`,
        background: active ? COLORS.accent : COLORS.panelBgSoft,
        color: active ? COLORS.strong : COLORS.text,
        padding: "8px 12px",
        borderRadius: 999,
        fontWeight: 800,
        cursor: "pointer",
      }}
    >
      {children}
    </button>
  );
}


function isAdminPath(path?: string) {
  if (!path) return false;
  return path.startsWith("/admin");
}

function sessionContainsAdmin(item: SessionListItem) {
  return (
    isAdminPath(item.landingPage) ||
    isAdminPath(item.exitPage) ||
    Boolean(item.referer && item.referer.includes("/admin"))
  );
}

function getMetricTone(
  label: string,
  value: number
): { text: string; background: string; color: string } {
  if (label === "bounceRate") {
    if (value >= 70) {
      return { text: "주의", background: COLORS.redBg, color: COLORS.redText };
    }
    if (value >= 40) {
      return {
        text: "관찰",
        background: COLORS.yellowBg,
        color: COLORS.yellowText,
      };
    }
    return { text: "안정", background: COLORS.greenBg, color: COLORS.greenText };
  }

  if (label === "avgSessionSeconds") {
    if (value >= 120) {
      return { text: "양호", background: COLORS.greenBg, color: COLORS.greenText };
    }
    if (value >= 30) {
      return {
        text: "보통",
        background: COLORS.yellowBg,
        color: COLORS.yellowText,
      };
    }
    return { text: "짧음", background: COLORS.redBg, color: COLORS.redText };
  }

  if (label === "errorSessions") {
    if (value > 0) {
      return { text: "점검 필요", background: COLORS.redBg, color: COLORS.redText };
    }
    return { text: "정상", background: COLORS.greenBg, color: COLORS.greenText };
  }

  return { text: "참고", background: COLORS.blueBg, color: COLORS.blueText };
}


export default function AdminMonitorPage() {
  const [system, setSystem] = useState<SystemStatus | null>(null);
  const [traffic, setTraffic] = useState<TrafficStatus | null>(null);
  const [dailyTraffic, setDailyTraffic] = useState<DailyTrafficStatus | null>(
    null
  );
  const [sessions, setSessions] = useState<SessionListResponse | null>(null);
  const [flows, setFlows] = useState<FlowResponse | null>(null);
  const [selectedSessionId, setSelectedSessionId] = useState<string | null>(
    null
  );
  const [selectedSessionDetail, setSelectedSessionDetail] =
    useState<SessionDetailResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionDetailLoading, setSessionDetailLoading] = useState(false);
  const [windowMinutes, setWindowMinutes] = useState(DEFAULT_WINDOW_MINUTES);
  const [sessionFilter, setSessionFilter] = useState<SessionFilterType>("all");
  const [sessionSort, setSessionSort] = useState<SessionSortType>("problemFirst");
  const [hideAdminTraffic, setHideAdminTraffic] = useState(true);
  const [excelDownloading, setExcelDownloading] = useState(false);
  const [excelDownloadError, setExcelDownloadError] = useState<string | null>(null);

  const { isMobile, isTablet } = useViewport();

  const cardStyle = useMemo(() => getCardStyle(isMobile), [isMobile]);
  const sectionTitle = useMemo(
    () => getSectionTitleStyle(isMobile),
    [isMobile]
  );

  const topIps = safeArray(dailyTraffic?.topIps);
  const topStayIps = safeArray(dailyTraffic?.topStayIps);
  const referers = safeArray(dailyTraffic?.referers);
  const hourlyStats = safeArray(dailyTraffic?.hourlyStats);

  const fetchJson = async <T,>(url: string): Promise<T | null> => {
    try {
      const response = await api.get<T>(url);
      return response.data;
    } catch (error: any) {
      const status = error?.response?.status;
      const statusText = error?.response?.statusText;
      return {
        error:
          status != null
            ? `${status}${statusText ? ` ${statusText}` : ""}`
            : error instanceof Error
            ? error.message
            : "request failed",
      } as T;
    }
  };

  const fetchAll = async () => {
    try {
      const [systemJson, trafficJson, dailyJson, sessionsJson, flowsJson] =
        await Promise.all([
          fetchJson<SystemStatus>("/admin/monitor/system"),
          fetchJson<TrafficStatus>("/admin/monitor/traffic"),
          fetchJson<DailyTrafficStatus>("/admin/monitor/traffic/daily"),
          fetchJson<SessionListResponse>(
            `/admin/monitor/sessions?windowMinutes=${windowMinutes}`
          ),
          fetchJson<FlowResponse>(
            `/admin/monitor/flows?windowMinutes=${windowMinutes}`
          ),
        ]);

      setSystem(systemJson);
      setTraffic(trafficJson);
      setDailyTraffic(dailyJson);
      setSessions(sessionsJson);
      setFlows(flowsJson);

      const nextSessionId =
        selectedSessionId ||
        safeArray(sessionsJson?.sessions)[0]?.sessionId ||
        null;

      setSelectedSessionId(nextSessionId);
    } finally {
      setLoading(false);
    }
  };

  const fetchSessionDetail = async (sessionId: string) => {
    setSessionDetailLoading(true);
    try {
      const detail = await fetchJson<SessionDetailResponse>(
        `/admin/monitor/sessions/${encodeURIComponent(sessionId)}`
      );
      setSelectedSessionDetail(detail);
    } finally {
      setSessionDetailLoading(false);
    }
  };

  const downloadStockScreenerExcel = async () => {
    if (excelDownloading) return;

    setExcelDownloading(true);
    setExcelDownloadError(null);

    try {
      const response = await api.get<Blob>("/admin/monitor/stock-screener/excel", {
        responseType: "blob",
        headers: {
          Accept:
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        },
      });

      const blob = response.data;
      const disposition = String(response.headers?.["content-disposition"] || "");
      const utf8Name = disposition.match(/filename\*=UTF-8''([^;]+)/i);
      const simpleName = disposition.match(/filename="?([^";]+)"?/i);
      const fileName = decodeURIComponent(
        utf8Name?.[1] ||
          simpleName?.[1] ||
          `policy-intel-stock-screener-${new Date()
            .toISOString()
            .slice(0, 10)}.xlsx`
      );

      const objectUrl = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = fileName;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(objectUrl);
    } catch (error) {
      setExcelDownloadError(
        error instanceof Error ? error.message : "엑셀 다운로드에 실패했습니다."
      );
    } finally {
      setExcelDownloading(false);
    }
  };

  const fetchAllCurrent = useRef(fetchAll);
  fetchAllCurrent.current = fetchAll;
  const fetchSessionDetailCurrent = useRef(fetchSessionDetail);
  fetchSessionDetailCurrent.current = fetchSessionDetail;
  useEffect(() => {
    fetchAllCurrent.current();
    const timer = setInterval(() => fetchAllCurrent.current(), POLL_MS);
    return () => clearInterval(timer);
  }, [windowMinutes]);

  useEffect(() => {
    if (selectedSessionId) {
      fetchSessionDetailCurrent.current(selectedSessionId);
    } else {
      setSelectedSessionDetail(null);
    }
  }, [selectedSessionId]);

  const hasDailyError = Boolean(dailyTraffic?.error);
  const hasSessionError = Boolean(sessions?.error);
  const hasFlowError = Boolean(flows?.error);

  const rawSessionItems = safeArray(sessions?.sessions);

  const visibleSessionItems = useMemo(() => {
    let items = [...rawSessionItems];

    if (hideAdminTraffic) {
      items = items.filter((item) => !sessionContainsAdmin(item));
    }

    switch (sessionFilter) {
      case "active":
        items = items.filter((item) => item.active);
        break;
      case "error":
        items = items.filter((item) => item.hadError);
        break;
      case "bounce":
        items = items.filter((item) => (item.pageViewCount || 0) <= 1);
        break;
      case "adminExcluded":
        items = items.filter((item) => !sessionContainsAdmin(item));
        break;
      case "botOnly":
        items = items.filter((item) => item.botSuspected);
        break;
      default:
        break;
    }

    items.sort((a, b) => {
      if (sessionSort === "latest") {
        return new Date(b.lastSeen).getTime() - new Date(a.lastSeen).getTime();
      }
      if (sessionSort === "duration") {
        return (b.durationSeconds || 0) - (a.durationSeconds || 0);
      }
      if (sessionSort === "requests") {
        return (b.requestCount || 0) - (a.requestCount || 0);
      }

      const score = (item: SessionListItem) => {
        let s = 0;
        if (item.hadError) s += 100;
        if (item.botSuspected) s += 40;
        if ((item.pageViewCount || 0) <= 1) s += 30;
        if (item.active) s += 10;
        s += Math.min(item.requestCount || 0, 20);
        return s;
      };
      return score(b) - score(a);
    });

    return items;
  }, [rawSessionItems, hideAdminTraffic, sessionFilter, sessionSort]);

  const filteredFlows = useMemo(() => {
    const items = safeArray(flows?.topFlows);
    if (!hideAdminTraffic) return items;
    return items.filter((flow) => !flow.path.some((p) => isAdminPath(p)));
  }, [flows, hideAdminTraffic]);

  const filteredTopUrls = useMemo(() => {
    const items = safeArray(traffic?.topUrls);
    if (!hideAdminTraffic) return items;
    return items.filter((item) => !isAdminPath(item.url));
  }, [traffic, hideAdminTraffic]);

  const filteredLandingPages = useMemo(() => {
    const items = safeArray(traffic?.topLandingPages);
    if (!hideAdminTraffic) return items;
    return items.filter((item) => !isAdminPath(item.url));
  }, [traffic, hideAdminTraffic]);

  const filteredExitPages = useMemo(() => {
    const items = safeArray(traffic?.topExitPages);
    if (!hideAdminTraffic) return items;
    return items.filter((item) => !isAdminPath(item.url));
  }, [traffic, hideAdminTraffic]);

  useEffect(() => {
    const exists = visibleSessionItems.some(
      (item) => item.sessionId === selectedSessionId
    );
    if (!exists) {
      setSelectedSessionId(visibleSessionItems[0]?.sessionId || null);
    }
  }, [visibleSessionItems, selectedSessionId]);

  const selectedSessionSummary = useMemo(
    () =>
      visibleSessionItems.find((item) => item.sessionId === selectedSessionId) ||
      null,
    [visibleSessionItems, selectedSessionId]
  );

  const alertItems = useMemo(() => {
    const list: { level: "red" | "yellow" | "green"; text: string }[] = [];

    const errorSessions = sessions?.summary?.errorSessions ?? 0;
    const activeSessions = sessions?.summary?.activeSessions ?? 0;
    const bounceRate = dailyTraffic?.summary?.bounceRate ?? 0;
    const disk = system?.disk?.usedPercent ?? 0;
    const memUsed = system?.memory?.usedMb ?? 0;
    const memTotal = system?.memory?.totalMb ?? 1;
    const memPct = (memUsed / memTotal) * 100;

    if (errorSessions > 0) {
      list.push({
        level: "red",
        text: `에러 포함 세션 ${errorSessions}건`,
      });
    }

    if (bounceRate >= 60) {
      list.push({
        level: "yellow",
        text: `이탈률 ${formatPercent(bounceRate)}로 높음`,
      });
    }

    if (disk >= 85) {
      list.push({
        level: "red",
        text: `디스크 사용률 ${disk}%`,
      });
    }

    if (memPct >= 85) {
      list.push({
        level: "yellow",
        text: `메모리 사용률 ${memPct.toFixed(1)}%`,
      });
    }

    if (activeSessions > 0 && errorSessions === 0 && bounceRate < 40 && disk < 85) {
      list.push({
        level: "green",
        text: "현재 주요 지표는 안정 범위",
      });
    }

    return list.slice(0, 4);
  }, [sessions, dailyTraffic, system]);

  if (loading) {
    return (
      <div
        style={{
          padding: isMobile ? 16 : 24,
          color: COLORS.title,
          background: COLORS.pageBg,
          minHeight: "100vh",
        }}
      >
        불러오는 중...
      </div>
    );
  }

  return (
    <div
      style={{
        background: COLORS.pageBg,
        minHeight: "100vh",
        padding: isMobile ? 12 : isTablet ? 18 : 24,
      }}
    >
      <div style={{ maxWidth: 1480, margin: "0 auto" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: isMobile ? "flex-start" : "center",
            gap: 12,
            flexDirection: isMobile ? "column" : "row",
            marginBottom: 18,
          }}
        >
          <div>
            <h1
              style={{
                fontSize: isMobile ? 22 : isTablet ? 25 : 28,
                fontWeight: 800,
                marginBottom: 8,
                color: COLORS.title,
                lineHeight: 1.25,
                wordBreak: "keep-all",
              }}
            >
              관리자 모니터 대시보드
            </h1>

            <p
              style={{
                color: COLORS.muted,
                marginBottom: 0,
                fontSize: isMobile ? 14 : 15,
                lineHeight: 1.5,
                wordBreak: "keep-all",
              }}
            >
              운영 상태와 사용자 흐름을 한 화면에서 확인하고, 종목 스크리너 결과를 엑셀로 내려받습니다.
            </p>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              alignItems: "center",
              flexWrap: "wrap",
            }}
          >
            {[10, 30, 60].map((value) => {
              const active = value === windowMinutes;
              return (
                <ToggleButton
                  key={value}
                  active={active}
                  onClick={() => setWindowMinutes(value)}
                >
                  최근 {value}분
                </ToggleButton>
              );
            })}

            <ToggleButton
              active={hideAdminTraffic}
              onClick={() => setHideAdminTraffic((v) => !v)}
            >
              {hideAdminTraffic ? "관리자 경로 제외중" : "관리자 경로 포함"}
            </ToggleButton>

            <button
              onClick={downloadStockScreenerExcel}
              disabled={excelDownloading}
              title="첨부된 종목 스크리너 쿼리 전체 결과를 엑셀로 다운로드합니다."
              style={{
                border: "1px solid #155eef",
                background: excelDownloading ? "#98a2b3" : "#155eef",
                color: "#ffffff",
                padding: "9px 14px",
                borderRadius: 10,
                fontWeight: 800,
                cursor: excelDownloading ? "not-allowed" : "pointer",
                boxShadow: "0 1px 2px rgba(16,24,40,0.08)",
              }}
            >
              {excelDownloading ? "엑셀 생성 중..." : "종목 분석 엑셀"}
            </button>

            <button
              onClick={fetchAll}
              style={{
                border: `1px solid ${COLORS.panelBorder}`,
                background: COLORS.panelBgSoft,
                color: COLORS.strong,
                padding: "8px 12px",
                borderRadius: 10,
                fontWeight: 800,
                cursor: "pointer",
              }}
            >
              새로고침
            </button>
          </div>
        </div>

        <AdminDeployPanel />

        {excelDownloadError && (
          <ErrorBox
            title="종목 분석 엑셀 다운로드 실패"
            message={excelDownloadError}
            hint="/api/admin/monitor/stock-screener/excel 응답과 백엔드 로그를 확인하세요."
          />
        )}

        {alertItems.length > 0 && (
          <div
            style={{
              ...cardStyle,
              marginBottom: 16,
              display: "flex",
              gap: 10,
              flexWrap: "wrap",
              alignItems: "center",
            }}
          >
            <strong style={{ color: COLORS.strong }}>운영 경고 요약</strong>
            {alertItems.map((item, idx) => {
              const background =
                item.level === "red"
                  ? COLORS.redBg
                  : item.level === "yellow"
                  ? COLORS.yellowBg
                  : COLORS.greenBg;
              const color =
                item.level === "red"
                  ? COLORS.redText
                  : item.level === "yellow"
                  ? COLORS.yellowText
                  : COLORS.greenText;

              return (
                <span
                  key={`${item.text}-${idx}`}
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    padding: "6px 12px",
                    borderRadius: 999,
                    background,
                    color,
                    fontWeight: 800,
                    fontSize: 13,
                  }}
                >
                  {item.text}
                </span>
              );
            })}
          </div>
        )}

        {(hasDailyError || hasSessionError || hasFlowError) && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: getGridColumns(
                isMobile,
                isTablet,
                "1fr 1fr 1fr",
                "1fr",
                "1fr"
              ),
              gap: 12,
              marginBottom: 16,
            }}
          >
            {hasDailyError && (
              <ErrorBox
                title="하루 상세 통계 소스 오류"
                message={dailyTraffic?.error || "daily traffic load failed"}
                hint="DB 수집 또는 집계 로직을 점검해야 합니다."
              />
            )}
            {hasSessionError && (
              <ErrorBox
                title="세션 목록 소스 오류"
                message={sessions?.error || "session list load failed"}
                hint="/api/admin/monitor/sessions 응답과 세션 집계 로직을 확인해야 합니다."
              />
            )}
            {hasFlowError && (
              <ErrorBox
                title="이동 경로 소스 오류"
                message={flows?.error || "flow load failed"}
                hint="/api/admin/monitor/flows 응답과 flow 집계 로직을 점검해야 합니다."
              />
            )}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <TablePanel
            title="운영 핵심 요약"
            subtitle="서버 자원과 오늘의 방문 지표를 한 표에서 확인합니다."
            minWidth={860}
            headers={[
              { label: "구분", minWidth: 110 },
              { label: "지표", minWidth: 150 },
              { label: "현재 값", minWidth: 150 },
              { label: "보조 정보", minWidth: 240 },
              { label: "판정", minWidth: 110 },
            ]}
          >
            <tr>
              <TableCell emphasize>시스템</TableCell>
              <TableCell>Load Average</TableCell>
              <TableCell emphasize>{system?.loadAverage || "-"}</TableCell>
              <TableCell>{system?.hostname || "-"} / {system?.user || "-"}</TableCell>
              <TableCell><StatusPill ok={true} text="확인" warn /></TableCell>
            </tr>
            <tr>
              <TableCell emphasize>시스템</TableCell>
              <TableCell>Memory</TableCell>
              <TableCell emphasize>
                {system?.memory?.usedMb ?? 0} / {system?.memory?.totalMb ?? 0} MB
              </TableCell>
              <TableCell>여유 {system?.memory?.freeMb ?? 0} MB</TableCell>
              <TableCell>
                <StatusPill
                  ok={
                    ((system?.memory?.usedMb ?? 0) /
                      Math.max(system?.memory?.totalMb ?? 1, 1)) *
                      100 <
                    85
                  }
                  warn={
                    ((system?.memory?.usedMb ?? 0) /
                      Math.max(system?.memory?.totalMb ?? 1, 1)) *
                      100 >=
                    70
                  }
                  text={`${(
                    ((system?.memory?.usedMb ?? 0) /
                      Math.max(system?.memory?.totalMb ?? 1, 1)) *
                    100
                  ).toFixed(1)}%`}
                />
              </TableCell>
            </tr>
            <tr>
              <TableCell emphasize>시스템</TableCell>
              <TableCell>Disk</TableCell>
              <TableCell emphasize>{system?.disk?.usedPercent ?? 0}%</TableCell>
              <TableCell>
                {system?.disk?.usedHuman ?? "-"} 사용 / {system?.disk?.availableHuman ?? "-"} 여유
              </TableCell>
              <TableCell>
                <StatusPill
                  ok={(system?.disk?.usedPercent ?? 0) < 85}
                  warn={(system?.disk?.usedPercent ?? 0) >= 70}
                  text={(system?.disk?.usedPercent ?? 0) >= 85 ? "점검" : "정상"}
                />
              </TableCell>
            </tr>
            <tr>
              <TableCell emphasize>실시간</TableCell>
              <TableCell>최근 {windowMinutes}분 요청</TableCell>
              <TableCell emphasize>{traffic?.summary?.totalRequests ?? 0}</TableCell>
              <TableCell>
                고유 IP {traffic?.summary?.uniqueIps ?? 0} / 세션 {traffic?.summary?.sessionCount ?? 0}
              </TableCell>
              <TableCell><StatusPill ok={true} text="집계" /></TableCell>
            </tr>
            <tr>
              <TableCell emphasize>오늘</TableCell>
              <TableCell>고유 IP</TableCell>
              <TableCell emphasize>{dailyTraffic?.summary?.uniqueIps ?? 0}</TableCell>
              <TableCell>방문자 {dailyTraffic?.summary?.uniqueVisitors ?? 0}</TableCell>
              <TableCell><StatusPill ok={true} text="집계" /></TableCell>
            </tr>
            <tr>
              <TableCell emphasize>오늘</TableCell>
              <TableCell>세션 수</TableCell>
              <TableCell emphasize>{dailyTraffic?.summary?.sessionCount ?? 0}</TableCell>
              <TableCell>1페이지 이탈 {dailyTraffic?.summary?.bounceSessions ?? 0}</TableCell>
              <TableCell><StatusPill ok={true} text="집계" /></TableCell>
            </tr>
            <tr>
              <TableCell emphasize>오늘</TableCell>
              <TableCell>평균 체류시간</TableCell>
              <TableCell emphasize>{formatSeconds(dailyTraffic?.summary?.avgSessionSeconds)}</TableCell>
              <TableCell>중간값 {formatSeconds(dailyTraffic?.summary?.medianSessionSeconds)}</TableCell>
              <TableCell>
                <StatusPill
                  ok={(dailyTraffic?.summary?.avgSessionSeconds ?? 0) >= 120}
                  warn={
                    (dailyTraffic?.summary?.avgSessionSeconds ?? 0) >= 30 &&
                    (dailyTraffic?.summary?.avgSessionSeconds ?? 0) < 120
                  }
                  text={getMetricTone(
                    "avgSessionSeconds",
                    dailyTraffic?.summary?.avgSessionSeconds ?? 0
                  ).text}
                />
              </TableCell>
            </tr>
            <tr>
              <TableCell emphasize>오늘</TableCell>
              <TableCell>이탈률</TableCell>
              <TableCell emphasize>{formatPercent(dailyTraffic?.summary?.bounceRate)}</TableCell>
              <TableCell>
                평균 페이지/세션 {Number(dailyTraffic?.summary?.avgPagesPerSession ?? 0).toFixed(2)}
              </TableCell>
              <TableCell>
                <StatusPill
                  ok={(dailyTraffic?.summary?.bounceRate ?? 0) < 40}
                  warn={
                    (dailyTraffic?.summary?.bounceRate ?? 0) >= 40 &&
                    (dailyTraffic?.summary?.bounceRate ?? 0) < 70
                  }
                  text={getMetricTone(
                    "bounceRate",
                    dailyTraffic?.summary?.bounceRate ?? 0
                  ).text}
                />
              </TableCell>
            </tr>
          </TablePanel>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1.2fr 0.8fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TablePanel
            title="서비스 상태"
            subtitle="systemd 서비스 활성 상태"
            minWidth={560}
            headers={[
              { label: "서비스", minWidth: 260 },
              { label: "활성 상태", minWidth: 120 },
              { label: "부팅 설정", minWidth: 120 },
            ]}
          >
            {safeArray(system?.services).length === 0 ? (
              <tr>
                <TableCell>-</TableCell>
                <TableCell>데이터 없음</TableCell>
                <TableCell>-</TableCell>
              </tr>
            ) : (
              safeArray(system?.services).map((svc) => (
                <tr key={svc.name}>
                  <TableCell emphasize>{svc.name}</TableCell>
                  <TableCell>
                    <StatusPill
                      ok={svc.active === "active"}
                      text={svc.active}
                    />
                  </TableCell>
                  <TableCell>{svc.enabled}</TableCell>
                </tr>
              ))
            )}
          </TablePanel>

          <TablePanel
            title="필수 파일 상태"
            subtitle="운영에 필요한 파일 존재 여부"
            minWidth={460}
            headers={[
              { label: "파일", minWidth: 240 },
              { label: "상태", minWidth: 100 },
            ]}
          >
            {[
              [".env", !!system?.files?.envExists],
              [".venv/bin/python", !!system?.files?.venvPythonExists],
              ["sector_keywords.json", !!system?.files?.sectorKeywordsExists],
            ].map(([label, ok]) => (
              <tr key={String(label)}>
                <TableCell emphasize>{label}</TableCell>
                <TableCell>
                  <StatusPill ok={!!ok} text={ok ? "OK" : "FAIL"} />
                </TableCell>
              </tr>
            ))}
          </TablePanel>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1.1fr 0.9fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TablePanel
            title="프로세스 상태"
            subtitle="주요 배치·수집 프로세스 실행 여부"
            minWidth={520}
            headers={[
              { label: "프로세스", minWidth: 280 },
              { label: "상태", minWidth: 120 },
            ]}
          >
            {[
              ["run_all_crawlers", system?.processes?.runAllCrawlers],
              ["test_kor_stock", system?.processes?.testKorStock],
              ["test3", system?.processes?.test3],
              ["run_backfill", system?.processes?.runBackfill],
              ["dart_description", system?.processes?.dartDescription],
              ["kor_stock_description", system?.processes?.korStockDescription],
              ["chrome_residual", system?.processes?.chromeResidual],
            ].map(([name, running]) => (
              <tr key={String(name)}>
                <TableCell emphasize>{name}</TableCell>
                <TableCell>
                  <StatusPill
                    ok={!!running}
                    text={running ? "RUNNING" : "STOPPED"}
                  />
                </TableCell>
              </tr>
            ))}
          </TablePanel>

          <TablePanel
            title={`최근 ${windowMinutes}분 세션 검증`}
            subtitle="트래픽 집계와 세션 품질 상태"
            minWidth={560}
            headers={[
              { label: "지표", minWidth: 220 },
              { label: "값", minWidth: 130 },
              { label: "판정", minWidth: 120 },
            ]}
          >
            {[
              ["고유 IP", traffic?.summary?.uniqueIps ?? 0, "참고"],
              ["추정 세션 수", traffic?.summary?.sessionCount ?? 0, "참고"],
              ["평균 세션 길이", formatSeconds(traffic?.summary?.avgSessionSeconds), "참고"],
              ["1페이지 세션 수", traffic?.summary?.singlePageSessions ?? 0, "참고"],
              ["활성 세션", sessions?.summary?.activeSessions ?? 0, "참고"],
            ].map(([label, value, status]) => (
              <tr key={String(label)}>
                <TableCell emphasize>{label}</TableCell>
                <TableCell>{value}</TableCell>
                <TableCell><StatusPill ok={true} text={String(status)} warn /></TableCell>
              </tr>
            ))}
            <tr>
              <TableCell emphasize>에러 포함 세션</TableCell>
              <TableCell>{sessions?.summary?.errorSessions ?? 0}</TableCell>
              <TableCell>
                <StatusPill
                  ok={(sessions?.summary?.errorSessions ?? 0) === 0}
                  text={getMetricTone(
                    "errorSessions",
                    sessions?.summary?.errorSessions ?? 0
                  ).text}
                />
              </TableCell>
            </tr>
          </TablePanel>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1fr 1fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TablePanel
            title="하루 유입 요약"
            subtitle={dailyTraffic?.date || "일 단위 집계"}
            minWidth={560}
            headers={[
              { label: "지표", minWidth: 240 },
              { label: "값", minWidth: 150 },
            ]}
          >
            {[
              ["총 요청 수", dailyTraffic?.summary?.totalRequests ?? 0],
              ["추정 방문자 수", dailyTraffic?.summary?.uniqueVisitors ?? 0],
              ["중간값 세션 길이", formatSeconds(dailyTraffic?.summary?.medianSessionSeconds)],
              ["평균 페이지/세션", Number(dailyTraffic?.summary?.avgPagesPerSession ?? 0).toFixed(2)],
              ["1페이지 이탈 세션", dailyTraffic?.summary?.bounceSessions ?? 0],
              [
                "최대 동시 유입 추정",
                `${dailyTraffic?.summary?.maxOnlineCount ?? 0}${
                  dailyTraffic?.summary?.maxOnlineMinute
                    ? ` (${dailyTraffic.summary.maxOnlineMinute})`
                    : ""
                }`,
              ],
            ].map(([label, value]) => (
              <tr key={String(label)}>
                <TableCell emphasize>{label}</TableCell>
                <TableCell>{value}</TableCell>
              </tr>
            ))}
          </TablePanel>

          <TablePanel
            title="유입 출처 상위"
            subtitle="Referer 기준"
            minWidth={620}
            headers={[
              { label: "순위", minWidth: 70, align: "center" },
              { label: "유입 출처", minWidth: 400 },
              { label: "건수", minWidth: 100, align: "right" },
            ]}
          >
            {referers.length === 0 ? (
              <tr>
                <TableCell align="center">-</TableCell>
                <TableCell>데이터 없음</TableCell>
                <TableCell align="right">0</TableCell>
              </tr>
            ) : (
              referers.map((item, idx) => (
                <tr key={`${item.source}-${idx}`}>
                  <TableCell align="center">{idx + 1}</TableCell>
                  <TableCell emphasize>{item.source}</TableCell>
                  <TableCell align="right" emphasize>{item.count}</TableCell>
                </tr>
              ))
            )}
          </TablePanel>
        </div>

        <div style={{ ...cardStyle, marginBottom: 16, overflowX: "auto" }}>
          <div
            style={{
              ...sectionTitle,
              display: "flex",
              justifyContent: "space-between",
              alignItems: isMobile ? "flex-start" : "center",
              gap: 10,
              flexDirection: isMobile ? "column" : "row",
            }}
          >
            <span>최근 {windowMinutes}분 세션 목록</span>
            <span
              style={{
                fontSize: 13,
                color: COLORS.muted,
                fontWeight: 600,
              }}
            >
              클릭하면 하단에 상세 타임라인이 열립니다
            </span>
          </div>

          <div
            style={{
              display: "flex",
              gap: 8,
              flexWrap: "wrap",
              marginBottom: 12,
              alignItems: "center",
            }}
          >
            {[
              ["all", "전체"],
              ["active", "활성"],
              ["error", "에러"],
              ["bounce", "1페이지 이탈"],
              ["adminExcluded", "관리자 제외"],
              ["botOnly", "BOT 의심"],
            ].map(([key, label]) => (
              <ToggleButton
                key={key}
                active={sessionFilter === key}
                onClick={() => setSessionFilter(key as SessionFilterType)}
              >
                {label}
              </ToggleButton>
            ))}

            <select
              value={sessionSort}
              onChange={(e) => setSessionSort(e.target.value as SessionSortType)}
              style={{
                marginLeft: isMobile ? 0 : 8,
                border: `1px solid ${COLORS.panelBorder}`,
                background: COLORS.panelBgSoft,
                color: COLORS.strong,
                padding: "9px 12px",
                borderRadius: 999,
                fontWeight: 700,
                outline: "none",
              }}
            >
              <option value="problemFirst">문제 우선</option>
              <option value="latest">최신순</option>
              <option value="duration">체류 긴 순</option>
              <option value="requests">요청 많은 순</option>
            </select>

            <span style={{ color: COLORS.muted, fontSize: 13 }}>
              표시 세션 {visibleSessionItems.length}건
            </span>
          </div>

          {hasSessionError ? (
            <ErrorBox
              title="세션 목록 불러오기 실패"
              message={sessions?.error || "sessions unavailable"}
              hint="세션 집계 API부터 확인해야 합니다."
            />
          ) : visibleSessionItems.length === 0 ? (
            <SectionEmpty text={`조건에 맞는 최근 ${windowMinutes}분 세션이 없습니다.`} />
          ) : (
            <table
              style={{
                width: "100%",
                borderCollapse: "collapse",
                minWidth: 1350,
              }}
            >
              <thead>
                <tr style={{ background: COLORS.tableHeader }}>
                  {[
                    "상태",
                    "세션 ID",
                    "IP",
                    "디바이스",
                    "첫 방문",
                    "마지막 활동",
                    "체류시간",
                    "요청",
                    "페이지뷰",
                    "랜딩",
                    "이탈",
                    "Referer",
                    "검증",
                  ].map((head) => (
                    <th
                      key={head}
                      style={{
                        textAlign: "left",
                        padding: "10px 12px",
                        color: COLORS.title,
                        fontSize: 13,
                      }}
                    >
                      {head}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {visibleSessionItems.map((item) => {
                  const selected = item.sessionId === selectedSessionId;
                  return (
                    <tr
                      key={item.sessionId}
                      onClick={() => setSelectedSessionId(item.sessionId)}
                      style={{
                        background: selected ? COLORS.selectedRow : undefined,
                        cursor: "pointer",
                      }}
                    >
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: `1px solid ${COLORS.divider}`,
                        }}
                      >
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {item.active ? (
                            <StatusPill ok={true} text="ACTIVE" />
                          ) : (
                            <StatusPill ok={false} text="ENDED" warn />
                          )}
                          {item.botSuspected ? (
                            <StatusPill ok={false} text="BOT?" />
                          ) : null}
                          {item.hadError ? (
                            <StatusPill ok={false} text="ERROR" />
                          ) : null}
                          {(item.pageViewCount || 0) <= 1 ? (
                            <StatusPill ok={true} text="BOUNCE" warn />
                          ) : null}
                        </div>
                      </td>
                      <TableCell minWidth={170} emphasize>
                        {item.sessionId}
                      </TableCell>
                      <TableCell minWidth={130}>{item.ip}</TableCell>
                      <TableCell minWidth={150}>
                        <div>{compactUserAgent(item.userAgent)}</div>
                        <div style={{ color: COLORS.muted, fontSize: 12 }}>
                          {item.country || "-"}
                          {item.city ? ` / ${item.city}` : ""}
                        </div>
                      </TableCell>
                      <TableCell minWidth={160}>
                        {formatDateTime(item.firstSeen)}
                      </TableCell>
                      <TableCell minWidth={160}>
                        {formatDateTime(item.lastSeen)}
                      </TableCell>
                      <TableCell emphasize>
                        {formatSeconds(item.durationSeconds)}
                      </TableCell>
                      <TableCell align="center" emphasize>
                        {item.requestCount}
                      </TableCell>
                      <TableCell align="center" emphasize>
                        {item.pageViewCount}
                      </TableCell>
                      <TableCell minWidth={170}>
                        {truncate(item.landingPage, 38)}
                      </TableCell>
                      <TableCell minWidth={170}>
                        {truncate(item.exitPage, 38)}
                      </TableCell>
                      <TableCell minWidth={190}>
                        {truncate(item.referer, 42)}
                      </TableCell>
                      <td
                        style={{
                          padding: "10px 12px",
                          borderBottom: `1px solid ${COLORS.divider}`,
                        }}
                      >
                        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                          {item.hadLogin ? (
                            <StatusPill ok={true} text="LOGIN" />
                          ) : null}
                          {item.hadPayment ? (
                            <StatusPill ok={true} text="PAY" />
                          ) : null}
                          {!item.hadLogin && !item.hadPayment && !item.hadError ? (
                            <StatusPill ok={true} text="NORMAL" />
                          ) : null}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1.2fr 0.8fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          <TablePanel
            title="선택 세션 상세"
            subtitle={
              selectedSessionSummary
                ? `${selectedSessionSummary.ip} / ${compactUserAgent(
                    selectedSessionSummary.userAgent
                  )}`
                : "세션을 선택하세요."
            }
            minWidth={920}
            headers={[
              { label: "순서", minWidth: 70, align: "center" },
              { label: "시각", minWidth: 160 },
              { label: "간격", minWidth: 90 },
              { label: "Method", minWidth: 90 },
              { label: "URL", minWidth: 320 },
              { label: "HTTP", minWidth: 90, align: "center" },
              { label: "유형", minWidth: 90, align: "center" },
              { label: "체류", minWidth: 110, align: "right" },
              { label: "Referer", minWidth: 260 },
            ]}
          >
            {!selectedSessionId ? (
              <tr>
                <TableCell align="center">-</TableCell>
                <TableCell>선택된 세션 없음</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell>-</TableCell>
              </tr>
            ) : sessionDetailLoading ? (
              <tr>
                <TableCell align="center">-</TableCell>
                <TableCell>불러오는 중</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell>-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="center">-</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell>-</TableCell>
              </tr>
            ) : (
              safeArray(selectedSessionDetail?.events).map((event, idx, arr) => {
                const prev = idx > 0 ? arr[idx - 1] : null;
                const gapSeconds = prev
                  ? Math.max(
                      0,
                      Math.floor(
                        (new Date(event.time).getTime() -
                          new Date(prev.time).getTime()) /
                          1000
                      )
                    )
                  : 0;
                return (
                  <tr key={`${event.time}-${idx}`}>
                    <TableCell align="center" emphasize>{idx + 1}</TableCell>
                    <TableCell>{formatDateTime(event.time)}</TableCell>
                    <TableCell>{prev ? `+${formatSeconds(gapSeconds)}` : "START"}</TableCell>
                    <TableCell emphasize>{event.method}</TableCell>
                    <TableCell>{event.url}</TableCell>
                    <TableCell align="center">
                      <StatusPill
                        ok={event.status >= 200 && event.status < 400}
                        warn={event.status >= 400 && event.status < 500}
                        text={String(event.status)}
                      />
                    </TableCell>
                    <TableCell align="center">{event.isApi ? "API" : "PAGE"}</TableCell>
                    <TableCell align="right" emphasize>{formatSeconds(event.staySeconds)}</TableCell>
                    <TableCell>{event.referer || "-"}</TableCell>
                  </tr>
                );
              })
            )}
          </TablePanel>

          <TablePanel
            title={`최근 ${windowMinutes}분 상위 이동 경로`}
            subtitle="사용자 이동 흐름"
            minWidth={700}
            headers={[
              { label: "순위", minWidth: 70, align: "center" },
              { label: "경로", minWidth: 360 },
              { label: "횟수", minWidth: 90, align: "right" },
              { label: "평균 체류", minWidth: 120, align: "right" },
              { label: "이탈률", minWidth: 100, align: "right" },
            ]}
          >
            {filteredFlows.length === 0 ? (
              <tr>
                <TableCell align="center">-</TableCell>
                <TableCell>데이터 없음</TableCell>
                <TableCell align="right">0</TableCell>
                <TableCell align="right">-</TableCell>
                <TableCell align="right">-</TableCell>
              </tr>
            ) : (
              filteredFlows.map((flow, idx) => (
                <tr key={`${flow.path.join(" > ")}-${idx}`}>
                  <TableCell align="center" emphasize>{idx + 1}</TableCell>
                  <TableCell emphasize>{flow.path.join(" → ")}</TableCell>
                  <TableCell align="right" emphasize>{flow.count}</TableCell>
                  <TableCell align="right">{formatSeconds(flow.avgDurationSeconds)}</TableCell>
                  <TableCell align="right">{formatPercent(flow.bounceRate)}</TableCell>
                </tr>
              ))
            )}
          </TablePanel>
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1fr 1fr 1fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          {[
            ["상위 URL", filteredTopUrls],
            ["랜딩 페이지", filteredLandingPages],
            ["이탈 페이지", filteredExitPages],
          ].map(([title, rows]) => (
            <TablePanel
              key={String(title)}
              title={String(title)}
              minWidth={520}
              headers={[
                { label: "순위", minWidth: 70, align: "center" },
                { label: "URL", minWidth: 330 },
                { label: "건수", minWidth: 90, align: "right" },
              ]}
            >
              {(rows as { url: string; count: number }[]).length === 0 ? (
                <tr>
                  <TableCell align="center">-</TableCell>
                  <TableCell>데이터 없음</TableCell>
                  <TableCell align="right">0</TableCell>
                </tr>
              ) : (
                (rows as { url: string; count: number }[]).map((item, idx) => (
                  <tr key={`${item.url}-${idx}`}>
                    <TableCell align="center">{idx + 1}</TableCell>
                    <TableCell emphasize>{item.url}</TableCell>
                    <TableCell align="right" emphasize>{item.count}</TableCell>
                  </tr>
                ))
              )}
            </TablePanel>
          ))}
        </div>

        <div
          style={{
            display: "grid",
            gridTemplateColumns: getGridColumns(
              isMobile,
              isTablet,
              "1fr 1fr",
              "1fr",
              "1fr"
            ),
            gap: 12,
            marginBottom: 16,
          }}
        >
          {isMobile ? (
            <div
              style={{
                background: COLORS.panelBg,
                borderRadius: 14,
                border: `1px solid ${COLORS.panelBorder}`,
                overflow: "hidden",
              }}
            >
              <div
                style={{
                  padding: "14px 16px 12px",
                  borderBottom: `1px solid ${COLORS.panelBorder}`,
                }}
              >
                <div
                  style={{
                    color: COLORS.title,
                    fontSize: 16,
                    fontWeight: 800,
                  }}
                >
                  오래 머문 방문자 상위
                </div>
                <div
                  style={{
                    color: COLORS.muted,
                    fontSize: 12,
                    marginTop: 4,
                  }}
                >
                  방문자 ID와 핵심 체류 정보만 표시합니다.
                </div>
              </div>

              {topStayIps.length === 0 ? (
                <div style={{ padding: 14, color: COLORS.muted }}>
                  데이터가 없습니다.
                </div>
              ) : (
                topStayIps.map((item, idx) => (
                  <table
                    key={`${item.ip}-${idx}`}
                    style={{
                      width: "100%",
                      borderCollapse: "collapse",
                      tableLayout: "fixed",
                      borderBottom:
                        idx === topStayIps.length - 1
                          ? "none"
                          : `1px solid ${COLORS.panelBorder}`,
                    }}
                  >
                    <tbody>
                      <tr>
                        <TableCell emphasize>순위 / 방문자</TableCell>
                        <TableCell align="right">
                          {idx + 1}위 ·{" "}
                          <span title={item.ip}>
                            {formatVisitorId(item.ip)}
                          </span>
                        </TableCell>
                      </tr>
                      <tr>
                        <TableCell emphasize>랜딩 페이지</TableCell>
                        <TableCell align="right">
                          <span title={item.landingPage}>
                            {truncate(item.landingPage, 28)}
                          </span>
                        </TableCell>
                      </tr>
                      <tr>
                        <TableCell emphasize>최대 체류</TableCell>
                        <TableCell align="right">
                          {formatSeconds(
                            item.maxSessionSeconds || item.avgSessionSeconds
                          )}
                        </TableCell>
                      </tr>
                    </tbody>
                  </table>
                ))
              )}
            </div>
          ) : (
            <TablePanel
              title="오래 머문 방문자 상위"
              subtitle="원본 IP 또는 해시값은 짧은 방문자 ID로 표시합니다."
              minWidth={560}
              headers={[
                { label: "순위", minWidth: 70, align: "center" },
                { label: "방문자 ID", minWidth: 120 },
                { label: "랜딩 페이지", minWidth: 260 },
                { label: "최대 체류", minWidth: 120, align: "right" },
              ]}
            >
              {topStayIps.length === 0 ? (
                <tr>
                  <TableCell align="center">-</TableCell>
                  <TableCell>데이터 없음</TableCell>
                  <TableCell>-</TableCell>
                  <TableCell align="right">-</TableCell>
                </tr>
              ) : (
                topStayIps.map((item, idx) => (
                  <tr key={`${item.ip}-${idx}`}>
                    <TableCell align="center">{idx + 1}</TableCell>
                    <TableCell emphasize>
                      <span title={item.ip}>{formatVisitorId(item.ip)}</span>
                    </TableCell>
                    <TableCell>
                      <span title={item.landingPage}>
                        {truncate(item.landingPage, 36)}
                      </span>
                    </TableCell>
                    <TableCell align="right" emphasize>
                      {formatSeconds(
                        item.maxSessionSeconds || item.avgSessionSeconds
                      )}
                    </TableCell>
                  </tr>
                ))
              )}
            </TablePanel>
          )}

          <TablePanel
            title="시간대별 유입"
            minWidth={isMobile ? 0 : 600}
            headers={[
              { label: "시간", minWidth: isMobile ? 70 : 100 },
              { label: "요청", minWidth: isMobile ? 70 : 100, align: "right" },
              { label: "고유 IP", minWidth: isMobile ? 70 : 100, align: "right" },
              { label: "세션", minWidth: isMobile ? 70 : 100, align: "right" },
            ]}
          >
            {hourlyStats.length === 0 ? (
              <tr>
                <TableCell>-</TableCell>
                <TableCell align="right">0</TableCell>
                <TableCell align="right">0</TableCell>
                <TableCell align="right">0</TableCell>
              </tr>
            ) : (
              hourlyStats.map((item) => (
                <tr key={item.hour}>
                  <TableCell emphasize>{item.hour}</TableCell>
                  <TableCell align="right">{item.requests}</TableCell>
                  <TableCell align="right">{item.uniqueIps}</TableCell>
                  <TableCell align="right">{item.sessions}</TableCell>
                </tr>
              ))
            )}
          </TablePanel>
        </div>

        {topIps.length > 0 && (
          <div style={{ marginBottom: 16 }}>
            {isMobile ? (
              <div
                style={{
                  background: COLORS.panelBg,
                  borderRadius: 14,
                  border: `1px solid ${COLORS.panelBorder}`,
                  overflow: "hidden",
                }}
              >
                <div
                  style={{
                    padding: "14px 16px 12px",
                    borderBottom: `1px solid ${COLORS.panelBorder}`,
                  }}
                >
                  <div
                    style={{
                      color: COLORS.title,
                      fontSize: 16,
                      fontWeight: 800,
                    }}
                  >
                    방문자별 상세 방문 내역
                  </div>
                  <div
                    style={{
                      color: COLORS.muted,
                      fontSize: 12,
                      marginTop: 4,
                    }}
                  >
                    모바일에서는 핵심 정보만 세로형 표로 표시합니다.
                  </div>
                </div>

                {topIps.map((item, idx) => (
                  <div
                    key={`${item.ip}-${idx}`}
                    style={{
                      padding: 14,
                      borderBottom:
                        idx === topIps.length - 1
                          ? "none"
                          : `1px solid ${COLORS.panelBorder}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 10,
                        alignItems: "center",
                        marginBottom: 10,
                      }}
                    >
                      <div style={{ minWidth: 0 }}>
                        <div
                          title={item.ip}
                          style={{
                            color: COLORS.strong,
                            fontSize: 14,
                            fontWeight: 800,
                          }}
                        >
                          {formatVisitorId(item.ip)}
                        </div>
                        <div
                          style={{
                            color: COLORS.muted,
                            fontSize: 11,
                            marginTop: 2,
                          }}
                        >
                          방문자 {idx + 1}
                        </div>
                      </div>
                      <StatusPill
                        ok={!item.botSuspected}
                        text={item.botSuspected ? "BOT 의심" : "정상"}
                      />
                    </div>

                    <table
                      style={{
                        width: "100%",
                        borderCollapse: "collapse",
                        tableLayout: "fixed",
                      }}
                    >
                      <tbody>
                        <tr>
                          <TableCell emphasize>요청 / 세션</TableCell>
                          <TableCell align="right">
                            {item.requestCount} / {item.sessionCount}
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>페이지뷰</TableCell>
                          <TableCell align="right">
                            {item.pageViewCount}
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>방문 시간</TableCell>
                          <TableCell align="right">
                            <div>{formatDateTime(item.firstSeen)}</div>
                            <div
                              style={{
                                color: COLORS.muted,
                                fontSize: 11,
                                marginTop: 2,
                              }}
                            >
                              마지막 {formatDateTime(item.lastSeen)}
                            </div>
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>체류시간</TableCell>
                          <TableCell align="right">
                            평균 {formatSeconds(item.avgSessionSeconds)}
                            <div
                              style={{
                                color: COLORS.muted,
                                fontSize: 11,
                                marginTop: 2,
                              }}
                            >
                              최대 {formatSeconds(item.maxSessionSeconds)}
                            </div>
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>랜딩</TableCell>
                          <TableCell align="right">
                            <span title={item.landingPage}>
                              {truncate(item.landingPage, 28)}
                            </span>
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>이탈</TableCell>
                          <TableCell align="right">
                            <span title={item.exitPage}>
                              {truncate(item.exitPage, 28)}
                            </span>
                          </TableCell>
                        </tr>
                        <tr>
                          <TableCell emphasize>유입 출처</TableCell>
                          <TableCell align="right">
                            <span title={item.referer}>
                              {truncate(item.referer, 28)}
                            </span>
                          </TableCell>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                ))}
              </div>
            ) : (
              <TablePanel
                title="방문자별 상세 방문 내역"
                subtitle="원본 IP 또는 해시값은 짧은 방문자 ID로 표시합니다."
                minWidth={1040}
                headers={[
                  { label: "방문자 ID", minWidth: 120 },
                  { label: "요청", minWidth: 70, align: "right" },
                  { label: "세션", minWidth: 70, align: "right" },
                  { label: "페이지뷰", minWidth: 80, align: "right" },
                  { label: "첫 방문", minWidth: 150 },
                  { label: "마지막 활동", minWidth: 150 },
                  { label: "평균/최대 체류", minWidth: 150 },
                  { label: "랜딩 → 이탈", minWidth: 260 },
                  { label: "Referer", minWidth: 180 },
                  { label: "상태", minWidth: 90 },
                ]}
              >
                {topIps.map((item, idx) => (
                  <tr key={`${item.ip}-${idx}`}>
                    <TableCell minWidth={120} emphasize>
                      <span title={item.ip}>{formatVisitorId(item.ip)}</span>
                    </TableCell>
                    <TableCell align="right" emphasize>
                      {item.requestCount}
                    </TableCell>
                    <TableCell align="right" emphasize>
                      {item.sessionCount}
                    </TableCell>
                    <TableCell align="right" emphasize>
                      {item.pageViewCount}
                    </TableCell>
                    <TableCell minWidth={150}>
                      {formatDateTime(item.firstSeen)}
                    </TableCell>
                    <TableCell minWidth={150}>
                      {formatDateTime(item.lastSeen)}
                    </TableCell>
                    <TableCell minWidth={150}>
                      <div>평균 {formatSeconds(item.avgSessionSeconds)}</div>
                      <div
                        style={{
                          color: COLORS.muted,
                          fontSize: 11,
                          marginTop: 2,
                        }}
                      >
                        최대 {formatSeconds(item.maxSessionSeconds)}
                      </div>
                    </TableCell>
                    <TableCell minWidth={260}>
                      <div title={item.landingPage}>
                        {truncate(item.landingPage, 34)}
                      </div>
                      <div
                        title={item.exitPage}
                        style={{
                          color: COLORS.muted,
                          fontSize: 11,
                          marginTop: 3,
                        }}
                      >
                        → {truncate(item.exitPage, 34)}
                      </div>
                    </TableCell>
                    <TableCell minWidth={180}>
                      <span title={item.referer}>
                        {truncate(item.referer, 32)}
                      </span>
                    </TableCell>
                    <TableCell>
                      <StatusPill
                        ok={!item.botSuspected}
                        text={item.botSuspected ? "BOT?" : "OK"}
                      />
                    </TableCell>
                  </tr>
                ))}
              </TablePanel>
            )}
          </div>
        )}

        <div style={{ marginBottom: 16 }}>
          <TablePanel
            title="상태코드 분포"
            subtitle="최근 요청의 HTTP 상태코드 집계"
            minWidth={520}
            headers={[
              { label: "HTTP 상태", minWidth: 160 },
              { label: "요청 수", minWidth: 120, align: "right" },
              { label: "판정", minWidth: 120 },
            ]}
          >
            {Object.entries(traffic?.statusCounts || {}).length === 0 ? (
              <tr>
                <TableCell>-</TableCell>
                <TableCell align="right">0</TableCell>
                <TableCell>데이터 없음</TableCell>
              </tr>
            ) : (
              Object.entries(traffic?.statusCounts || {}).map(([code, count]) => (
                <tr key={code}>
                  <TableCell emphasize>HTTP {code}</TableCell>
                  <TableCell align="right" emphasize>{count}</TableCell>
                  <TableCell>
                    <StatusPill
                      ok={code.startsWith("2") || code.startsWith("3")}
                      warn={code.startsWith("4")}
                      text={
                        code.startsWith("2")
                          ? "정상"
                          : code.startsWith("3")
                          ? "리다이렉트"
                          : code.startsWith("4")
                          ? "클라이언트 오류"
                          : "서버 오류"
                      }
                    />
                  </TableCell>
                </tr>
              ))
            )}
          </TablePanel>
        </div>

        <div
          style={{
            marginTop: 18,
            color: COLORS.muted,
            fontSize: 13,
            lineHeight: 1.7,
          }}
        >
          <div>System updated: {system?.timestamp || "-"}</div>
          <div>Traffic updated: {traffic?.timestamp || "-"}</div>
          <div>Daily traffic date: {dailyTraffic?.date || "-"}</div>
          <div>
            Session window: 최근 {windowMinutes}분 / Polling: {POLL_MS / 1000}초
          </div>
        </div>
      </div>
    </div>
  );
}
